import styled from '@emotion/styled';
import { spacer, color } from '@tokens';
import { shouldForwardProp } from '@utils/styledProps';

type DividerOrientations = 'horizontal' | 'vertical';
export type DividerSpacing = 'xs' | 's' | 'm' | 'l';

export interface DividerProps {
  /**
   * Set divider orientation: horizontal (default) or vertical
   */
  orientation?: DividerOrientations;

  /**
   * Spacing around the divider
   */
  spacing?: DividerSpacing;

  /**
   * Styles override.
   */
  className?: string;

  /**
   * Hide element from Accessibility API
   */
  ariaHidden?: boolean;
}

const SPACING_MAPPING: Record<DividerSpacing, string> = {
  xs: spacer.x1,
  s: spacer.x2,
  m: spacer.x3,
  l: spacer.x4,
};

function getSpacingValue(spacing: DividerSpacing): string {
  return SPACING_MAPPING[spacing];
}

interface HrProps {
  orientation: DividerOrientations;
  spacing: DividerSpacing;
}

const Hr = styled('hr', {
  shouldForwardProp: shouldForwardProp(['orientation', 'spacing']),
})<HrProps>`
  ${(props) => (props.orientation === 'horizontal' ? 'width' : 'height')}: 100%;
  border-width: 0;
  ${(props) =>
    props.orientation === 'horizontal'
      ? 'border-bottom-width'
      : 'border-left-width'}: 1px;
  border-image: initial;
  border-color: ${color.ui_border_light};
  border-style: solid;
  ${({ orientation, spacing }) => {
    const spacingValue = getSpacingValue(spacing);

    if (orientation === 'horizontal') {
      return `margin: ${spacingValue} 0`;
    }

    return `margin: 0 ${spacingValue}`;
  }}
`;

/**
 * A visual indicator to separate content into clear groups.
 *
 * If the vertical orientation is used, make sure that the parent element is assigned a height.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/2680b4-divider Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-divider--divider Code documentation}
 *
 * @example
 * <Divider />
 *
 * @example
 * <Divider orientation="vertical" />
 *
 * @example
 * <Divider spacing="l" />
 */
export const Divider = ({
  orientation = 'horizontal',
  spacing = 'xs',
  className,
  ariaHidden = false,
}: DividerProps): JSX.Element => {
  return (
    <Hr
      orientation={orientation}
      spacing={spacing}
      aria-orientation={orientation}
      className={className}
      aria-hidden={ariaHidden}
    />
  );
};

Divider.displayName = 'Divider';
